import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { CommonService } from 'src/@services/common.service';
import { ResponseStatus } from 'src/@shared/enum';
import { RESEND_COOLDOWN_SECONDS } from 'src/app/pages/login/auth-flow/code-cooldown';
import { PhoneChangeSendResult, PhoneChangeService } from './phone-change.service';

/** Digits only, so a stored "(555) 201-4567" and a masked "5552014567" compare equal. */
export function phoneDigits(value: string | null | undefined): string {
  return (value ?? '').replace(/\D/g, '');
}

type PhoneChangeStep = 'idle' | 'codeSent' | 'verified';

/**
 * The My Profile mobile-number field. A number that differs from the saved one must be verified here before the
 * profile can be saved: Verify texts a code to the new number, and a correct code saves it on the server.
 */
@Component({
  selector: 'app-phone-change-verify',
  templateUrl: './phone-change-verify.component.html',
  styleUrls: ['./phone-change-verify.component.scss'],
  standalone: false,
})
export class PhoneChangeVerifyComponent implements OnInit, OnChanges, OnDestroy {
  @Input() control!: FormControl;
  @Input() savedPhoneNumber: string | null = null;
  /** The newly verified number; the server has already saved it. */
  @Output() verified = new EventEmitter<string>();

  readonly codeControl = new FormControl('', { nonNullable: true });
  step: PhoneChangeStep = 'idle';
  sending = false;
  verifying = false;
  maskedPhone = '';
  sendError = '';
  codeError = '';
  cooldownSeconds = 0;

  _uiCanSend = false;
  /** The number differs from the saved one and has not been verified yet. */
  _uiNeedsVerify = false;
  _uiCodeSent = false;
  _uiVerified = false;
  _uiCooldown = false;
  _uiCodeIncomplete = true;

  private challengeId = '';
  private timer: ReturnType<typeof setInterval> | null = null;
  private subscriptions = new Subscription();

  constructor(public _cs: CommonService, private _phoneChange: PhoneChangeService) {}

  ngOnInit(): void {
    this.subscriptions.add(this.control.valueChanges.subscribe(() => this.onNumberEdited()));
    this.subscriptions.add(this.codeControl.valueChanges.subscribe((code) => {
      this._uiCodeIncomplete = phoneDigits(code).length !== 6;
    }));
    this.refreshUi();
  }

  ngOnChanges(): void {
    this.refreshUi();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.stopCooldown();
    this.control?.enable({ emitEvent: false });
  }

  send(): void {
    this.sending = true;
    this.sendError = '';
    this.refreshUi();
    this._phoneChange.send(phoneDigits(this.control.value)).subscribe({
      next: (response) => {
        this.sending = false;
        if (response.responseStatus == ResponseStatus.success && response.result?.challengeId) {
          this.onCodeSent(response.result);
        } else {
          this.sendError = response.message ?? '';
          this.refreshUi();
        }
      },
      error: () => this.resetToIdle(''),
    });
  }

  resend(): void {
    this.codeError = '';
    this._phoneChange.resend(this.challengeId).subscribe((response) => {
      if (response.responseStatus == ResponseStatus.success) {
        this.startCooldown(response.result?.retryAfterSeconds);
      } else if (response.result?.restart) {
        this.resetToIdle(response.message ?? '');
      } else {
        this.codeError = response.message ?? '';
        this.startCooldown(response.result?.retryAfterSeconds);
      }
    });
  }

  confirm(): void {
    if (this._uiCodeIncomplete || this.verifying) {
      return;
    }
    this.verifying = true;
    this.codeError = '';
    this._phoneChange.verify(this.challengeId, phoneDigits(this.codeControl.value)).subscribe({
      next: (response) => {
        this.verifying = false;
        if (response.responseStatus == ResponseStatus.success) {
          this.onVerified();
        } else if (response.result?.restart) {
          this.resetToIdle(response.message ?? '');
        } else {
          this.codeError = response.message ?? '';
          this.codeControl.reset();
        }
      },
      error: () => (this.verifying = false),
    });
  }

  /** Unlocks the number so the user can correct it; the code already sent stops being offered. */
  changeNumber(): void {
    this.resetToIdle('');
  }

  private onCodeSent(result: PhoneChangeSendResult): void {
    this.challengeId = result.challengeId ?? '';
    this.maskedPhone = result.maskedPhone ?? '';
    this.step = 'codeSent';
    this.codeError = '';
    this.codeControl.reset();
    this.control.disable({ emitEvent: false });
    this.startCooldown(result.retryAfterSeconds);
  }

  private onVerified(): void {
    this.step = 'verified';
    this.stopCooldown();
    this.control.enable({ emitEvent: false });
    this.refreshUi();
    this.verified.emit(phoneDigits(this.control.value));
  }

  private onNumberEdited(): void {
    if (this.step === 'verified') {
      this.step = 'idle';
    }
    this.sendError = '';
    this.refreshUi();
  }

  private resetToIdle(sendError: string): void {
    this.step = 'idle';
    this.sending = false;
    this.challengeId = '';
    this.codeError = '';
    this.sendError = sendError;
    this.stopCooldown();
    this.control.enable({ emitEvent: false });
    this.refreshUi();
  }

  private startCooldown(retryAfterSeconds?: number): void {
    this.stopCooldown();
    this.cooldownSeconds = retryAfterSeconds && retryAfterSeconds > 0 ? retryAfterSeconds : RESEND_COOLDOWN_SECONDS;
    this.timer = setInterval(() => {
      this.cooldownSeconds = Math.max(0, this.cooldownSeconds - 1);
      if (this.cooldownSeconds === 0) {
        this.stopCooldown();
      }
      this.refreshUi();
    }, 1000);
    this.refreshUi();
  }

  private stopCooldown(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  private refreshUi(): void {
    const changed = !!this.control && phoneDigits(this.control.value) !== phoneDigits(this.savedPhoneNumber);
    this._uiCodeSent = this.step === 'codeSent';
    this._uiVerified = this.step === 'verified' && !changed;
    this._uiNeedsVerify = this.step === 'idle' && changed;
    this._uiCanSend = this._uiNeedsVerify && !!this.control?.valid && phoneDigits(this.control.value).length >= 10 && !this.sending;
    this._uiCooldown = this.cooldownSeconds > 0;
  }
}
